/** Canonical error taxonomy, `<CATEGORY>_<SHORT_REASON>`, values STABLE because consumers branch on them. Self-keyed is load-bearing: the wire-value union derives from this map, so one mistyped value widens it with a code no producer throws.
 *  Per-member liveness is enforced by `QUAL-ERROR-CODES-MEMBER-LIVENESS-001`: a code no non-test surface names -- by accessor or by its identical wire literal -- is a dead declaration, retired rather than carried. */

import type { SelfKeyed } from '@federa/types/primitives-records';

const ERROR_CODES_SOURCE = {
	SEC_JWT_EXPIRED: 'SEC_JWT_EXPIRED',
	SEC_JWT_NOT_YET_VALID: 'SEC_JWT_NOT_YET_VALID',
	SEC_JWT_ISSUER_MISMATCH: 'SEC_JWT_ISSUER_MISMATCH',
	SEC_JWT_AUDIENCE_MISMATCH: 'SEC_JWT_AUDIENCE_MISMATCH',
	SEC_JWT_BAD_ALGORITHM: 'SEC_JWT_BAD_ALGORITHM',
	SEC_JWT_MISSING_KID: 'SEC_JWT_MISSING_KID',
	SEC_JWT_KEY_NOT_FOUND: 'SEC_JWT_KEY_NOT_FOUND',
	SEC_JWT_SIGNATURE_FAILED: 'SEC_JWT_SIGNATURE_FAILED',
	SEC_JWT_MALFORMED: 'SEC_JWT_MALFORMED',
	SEC_JWT_BAD_BASE64URL: 'SEC_JWT_BAD_BASE64URL',
	SEC_JWKS_URL_MISSING: 'SEC_JWKS_URL_MISSING',
	SEC_JWKS_PARSE_FAILED: 'SEC_JWKS_PARSE_FAILED',

	AUTH_NO_TOKEN: 'AUTH_NO_TOKEN',
	AUTH_INVALID_TOKEN: 'AUTH_INVALID_TOKEN',
	AUTH_CALLER_INVALID: 'AUTH_CALLER_INVALID',

	VAL_REQUIRED_FIELD: 'VAL_REQUIRED_FIELD',
	VAL_INVALID_INPUT: 'VAL_INVALID_INPUT',
	VAL_INVALID_IDENTIFIER: 'VAL_INVALID_IDENTIFIER',
	VAL_INVALID_ENUM: 'VAL_INVALID_ENUM',
	VAL_MISSING_DEPENDENCY: 'VAL_MISSING_DEPENDENCY',

	D1_BINDING_MISSING: 'D1_BINDING_MISSING',
	/** A Durable Object namespace slot the entry was asked to forward to is ABSENT or is bound to something that is not
	 *  a namespace. The third member of the binding-missing family beside the D1 and KV codes, and it exists for the same
	 *  reason: a forward that ASSERTED the slot instead of testing it failed later inside the stub getter with the
	 *  binding name already out of scope, so the operator got a stack with nothing to fix. */
	DO_BINDING_MISSING: 'DO_BINDING_MISSING',
	// KV: the store primitives (`tools/cache/kv-helpers.ts`) answer an ABSENT namespace and a missing key identically, so a
	// caller that must fail closed names the absent binding with its own code, and a degraded read/write with the other two.
	KV_BINDING_MISSING: 'KV_BINDING_MISSING',
	KV_READ_FAILED: 'KV_READ_FAILED',
	KV_WRITE_FAILED: 'KV_WRITE_FAILED',
	D1_QUERY_FAILED: 'D1_QUERY_FAILED',
	D1_INSERT_FAILED: 'D1_INSERT_FAILED',
	D1_UPDATE_FAILED: 'D1_UPDATE_FAILED',

	R2_BUCKET_INVALID: 'R2_BUCKET_INVALID',
	R2_FILE_SIZE_UNKNOWN: 'R2_FILE_SIZE_UNKNOWN',
	R2_FILE_SIZE_EXCEEDED: 'R2_FILE_SIZE_EXCEEDED',
	R2_PUT_FAILED: 'R2_PUT_FAILED',
	R2_STREAM_LIMIT_EXCEEDED: 'R2_STREAM_LIMIT_EXCEEDED',
	R2_SQL_QUERY_FAILED: 'R2_SQL_QUERY_FAILED',

	STRIPE_LEDGER_FAILED: 'STRIPE_LEDGER_FAILED',
	STRIPE_CONNECT_UPDATE_FAILED: 'STRIPE_CONNECT_UPDATE_FAILED',
	STRIPE_IDEMPOTENCY_KEY_MISSING: 'STRIPE_IDEMPOTENCY_KEY_MISSING',
	/** The refund REQUEST itself failed -- the one send a refund can be made by -- so only this code can sit over a
	 *  refund Stripe made (`refundDispatchError`, `isKnownRefundRefusal`: utils/payments/refund-outcome.ts). */
	STRIPE_REFUND_DISPATCH_FAILED: 'STRIPE_REFUND_DISPATCH_FAILED',

	TWILIO_SEND_FAILED: 'TWILIO_SEND_FAILED',
	TWILIO_API_ERROR: 'TWILIO_API_ERROR',

	COMM_PROFILE_LINK_FAILED: 'COMM_PROFILE_LINK_FAILED',

	BID_FIELD_REQUIRED: 'BID_FIELD_REQUIRED',
	BID_FETCH_FAILED: 'BID_FETCH_FAILED',
	BID_BALANCE_FETCH_FAILED: 'BID_BALANCE_FETCH_FAILED',
	BID_NO_ACTIVE_TRANSACTION: 'BID_NO_ACTIVE_TRANSACTION',

	AUC_BID_STATUS_FETCH_FAILED: 'AUC_BID_STATUS_FETCH_FAILED',
	AUC_DATE_FORMAT_INVALID: 'AUC_DATE_FORMAT_INVALID',
	AUC_INVALID_STATE: 'AUC_INVALID_STATE',
	AUC_INVALID_TRANSITION: 'AUC_INVALID_TRANSITION',

	/** A prepared flow declared an EMPTY step array. Since the implicit-entry change the graph's entry IS `steps[0]`, so an empty array has nothing to dispatch. Graph validation rejects the same shape earlier as NO_STEPS; this is the engine-side backstop that also carries the non-undefined narrowing. */
	FLOW_STEPS_EMPTY: 'FLOW_STEPS_EMPTY',
	FLOW_NODE_NOT_FOUND: 'FLOW_NODE_NOT_FOUND',
	FLOW_NODE_NO_NEXT: 'FLOW_NODE_NO_NEXT',
	FLOW_UNKNOWN_NODE_TYPE: 'FLOW_UNKNOWN_NODE_TYPE',
	FLOW_TASK_HANDLER_MISSING: 'FLOW_TASK_HANDLER_MISSING',
	FLOW_TASK_EXECUTION_FAILED: 'FLOW_TASK_EXECUTION_FAILED',
	FLOW_DECISION_NO_CONDITIONS: 'FLOW_DECISION_NO_CONDITIONS',
	FLOW_DECISION_NO_MATCH: 'FLOW_DECISION_NO_MATCH',
	FLOW_BRANCH_FAILED: 'FLOW_BRANCH_FAILED',
	FLOW_BRANCH_ALL_FAILED: 'FLOW_BRANCH_ALL_FAILED',
	FLOW_BRANCH_NO_JOIN: 'FLOW_BRANCH_NO_JOIN',
	FLOW_BRANCH_MAX_ITERATIONS: 'FLOW_BRANCH_MAX_ITERATIONS',
	FLOW_SPLIT_MISSING_BRANCHES: 'FLOW_SPLIT_MISSING_BRANCHES',
	FLOW_MAX_ITERATIONS: 'FLOW_MAX_ITERATIONS',
	FLOW_SUBFLOW_NOT_FOUND: 'FLOW_SUBFLOW_NOT_FOUND',
	FLOW_SUBFLOW_HANDLER_MISSING: 'FLOW_SUBFLOW_HANDLER_MISSING',
	FLOW_SUBFLOW_DEPTH_EXCEEDED: 'FLOW_SUBFLOW_DEPTH_EXCEEDED',
	FLOW_SUBFLOW_NAME_UNRESOLVED: 'FLOW_SUBFLOW_NAME_UNRESOLVED',
	FLOW_SUBFLOW_TARGET_UNRESOLVED: 'FLOW_SUBFLOW_TARGET_UNRESOLVED',
	FLOW_VALIDATION_REJECTED: 'FLOW_VALIDATION_REJECTED',
	/** A binding a flow needs is absent from `env` at runtime. TWO live throwers, and both are RESOLUTION failures
	 *  rather than resource errors: `createBindingGetter` (`utils/workers/service-binding-getter.ts`, the throw sits
	 *  in the module-private resolver its closure wraps) when a named service binding is undefined, and
	 *  `composeHostHandlerForward` (`tools/flow-engine/host-handler-forward.ts`)
	 *  when the env carries no capability-bridge origin. Neither is a D1 namespace, and the code carries no per-kind
	 *  discriminator -- the missing binding is named in the message, not in the code. It is thrown rather than
	 *  degraded so the failure routes through the flow-failure sink instead of surfacing as a null a caller reads as
	 *  an empty answer. */
	FLOW_BINDING_UNAVAILABLE: 'FLOW_BINDING_UNAVAILABLE',
	FLOW_REMOTE_FAILED: 'FLOW_REMOTE_FAILED',
	FLOW_REMOTE_INVALID_JSON: 'FLOW_REMOTE_INVALID_JSON',
	FLOW_REMOTE_TIMEOUT: 'FLOW_REMOTE_TIMEOUT',
	FLOW_TRIGGER_MISSING: 'FLOW_TRIGGER_MISSING',

	CONFIG_ENV_MISSING: 'CONFIG_ENV_MISSING',
	SECRET_NOT_FOUND: 'SECRET_NOT_FOUND',
	SECRET_EMPTY: 'SECRET_EMPTY',
	/** A PRESENT Secrets-Store binding did not supply a value -- its `.get()` threw, or answered empty. Distinct from
	 *  SECRET_NOT_FOUND on purpose: not-found means no source held the secret, this one means the DECLARED source was
	 *  unreadable, which must never be answered from a different source (operator security directive 2026-08-22). */
	SECRET_STORE_UNAVAILABLE: 'SECRET_STORE_UNAVAILABLE',
	/** A resolved secret's mode marker contradicts the deployment environment (e.g. an sk_test_ Stripe key in a declared-production worker, or an sk_live_ key anywhere production is not declared). */
	SECRET_MODE_MISMATCH: 'SECRET_MODE_MISMATCH',

	VEC_RECORD_REQUIRED_FIELDS: 'VEC_RECORD_REQUIRED_FIELDS',
	VEC_EMBED_GENERATION_FAILED: 'VEC_EMBED_GENERATION_FAILED',
	SEARCH_PROMPT_REQUIRED: 'SEARCH_PROMPT_REQUIRED',
	AI_SEARCH_ITEMS_BINDING_UNAVAILABLE: 'AI_SEARCH_ITEMS_BINDING_UNAVAILABLE',

	HTTP_FETCH_FAILED: 'HTTP_FETCH_FAILED',
	HTTP_JSON_PARSE_FAILED: 'HTTP_JSON_PARSE_FAILED',
	HTTP_REQUEST_FAILED: 'HTTP_REQUEST_FAILED',
	HTTP_UPGRADE_FAILED: 'HTTP_UPGRADE_FAILED',
	HTTP_STATUS_ASSERT_FAILED: 'HTTP_STATUS_ASSERT_FAILED',
	/** Default `AdminAPIError` code; a caller-specified `HTTP_CLIENT_ERROR_CODES.*` still wins. */
	HTTP_ADMIN_API_FAILED: 'HTTP_ADMIN_API_FAILED',
	/** WebSocket session setup or routing failure; rides the WS-error envelope. */
	WEBSOCKET_SESSION_ERROR: 'WEBSOCKET_SESSION_ERROR',

	USER_DETAILS_FETCH_FAILED: 'USER_DETAILS_FETCH_FAILED',
	USER_DETAILS_MISSING: 'USER_DETAILS_MISSING',
	PROP_REFUND_FAILED: 'PROP_REFUND_FAILED',
	PROP_OFFER_VIA_FLOW_FAILED: 'PROP_OFFER_VIA_FLOW_FAILED',
	PROP_DO_RPC_NOT_FOUND: 'PROP_DO_RPC_NOT_FOUND',

	ACTIVITY_FIELD_UNKNOWN: 'ACTIVITY_FIELD_UNKNOWN',

	TRANS_ID_REQUIRED: 'TRANS_ID_REQUIRED',
	TRANS_NOT_FOUND: 'TRANS_NOT_FOUND',
	TRANS_REFUND_INELIGIBLE: 'TRANS_REFUND_INELIGIBLE',

	EMBEDDING_QUERY_FAILED: 'EMBEDDING_QUERY_FAILED',

	DO_PERSIST_FAILED: 'DO_PERSIST_FAILED',
	DO_LOAD_FAILED: 'DO_LOAD_FAILED',

	TOOL_OPERATION_FAILED: 'TOOL_OPERATION_FAILED',
	/** The declared-symbol walk spent its rebuild budget before a member's rows were whole (iss-68aaf305): the rows it
	 *  built are kept and the NEXT process finishes the member. Stable so the replay host can answer the rule as pending
	 *  -- it measured nothing, and is asked again -- instead of reading it as a refusal or, worse, as a clean surface. */
	SCAN_INDEX_REBUILD_PENDING: 'SCAN_INDEX_REBUILD_PENDING',

	FS_READ_FAILED: 'FS_READ_FAILED',
	FS_WRITE_FAILED: 'FS_WRITE_FAILED',
	FS_LOCK_FAILED: 'FS_LOCK_FAILED',
	FS_PATH_INVALID: 'FS_PATH_INVALID',

	SUBPROCESS_SPAWN_FAILED: 'SUBPROCESS_SPAWN_FAILED',
	SUBPROCESS_NONZERO_EXIT: 'SUBPROCESS_NONZERO_EXIT',

	/** Migration apply refused against a prod target -- prod migrations are operator-run, never via the agent-facing flow. Stable so callers can disambiguate it from a generic 400. */
	MIGRATION_APPLY_PROD_REFUSED: 'MIGRATION_APPLY_PROD_REFUSED',
	/** Migration apply refused because a file's DDL is already live while its tracker row is missing -- the crash-before-record state. Stable so a retry can route to tracker-bootstrap instead of replaying landed DDL. */
	MIGRATION_TRACKER_OUT_OF_SYNC: 'MIGRATION_TRACKER_OUT_OF_SYNC',
	/** Migration apply refused because an open issue's `filePath` names a pending file -- a known defect is read before the
	 *  file runs (iss-migration-apply-reads-no-open-issue-naming-its-file-though-filepath-is-the-join-key). Stable so a
	 *  caller routes to the issue's own door, never to a flag: none lifts it. */
	MIGRATION_OPEN_ISSUE_NAMES_FILE: 'MIGRATION_OPEN_ISSUE_NAMES_FILE',
	/** A WRITING `migration-apply` verb reached without `confirm: true`. The surface is dry-run by default, so an
	 *  unconfirmed mutation is refused LOUDLY rather than performed: `repair` emits live `ALTER TABLE ... ADD COLUMN`
	 *  plus a selective DDL replay and `seed` writes tracker rows the drift audit reads. Stable so a caller can tell a
	 *  missing operator gate from an invalid target (`MIGRATION_APPLY_PROD_REFUSED`) or a generic 400. */
	MIGRATION_MUTATION_UNCONFIRMED: 'MIGRATION_MUTATION_UNCONFIRMED',
	/** A money-representation PHASE flow refused because the runtime it is executing in is not positively a development
	 *  one. The sibling of MIGRATION_APPLY_PROD_REFUSED for the flows that MOVE money rather than the schema: the gate
	 *  switch, the legacy backfill, the two verifications, the period cutoff and the evidence recorder. Stable so an
	 *  operator can tell "this stack is not the dev stack" from an ordinary invalid input. */
	MONEY_PHASE_PROD_REFUSED: 'MONEY_PHASE_PROD_REFUSED',

	APP_FETCH_FAILED: 'APP_FETCH_FAILED',
	APP_INVALID_INPUT: 'APP_INVALID_INPUT',

	/** A group's upward parent walk (`SELECT_GROUP_ROOT_ID_SQL`) answered NO root for a group the caller is a proven
	 *  active member of. A top-level group resolves to ITSELF, so the only ways here are a hard-deleted row mid-chain
	 *  or a cycle the sub-group admission gates are supposed to make impossible -- a defect state, never world scope.
	 *  The AUTHORING side throws it BEFORE any write; the retrieval side has its own non-fatal fail-closed answer
	 *  (`UNRESOLVED_ROOT_SCOPE_SENTINEL`) because a live call must not end on a malformed tree. */
	GROUP_ROOT_UNRESOLVED: 'GROUP_ROOT_UNRESOLVED',

	NOT_FOUND: 'NOT_FOUND',
	INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export const ERROR_CODES: SelfKeyed<typeof ERROR_CODES_SOURCE> = ERROR_CODES_SOURCE;
